import React from 'react'
import { Chip } from './Styled'
import { lightSecondary } from 'styles/theme'
import { secondary, dark } from 'styles/theme'
import { ratingColors7, ratingTexts2 } from 'keys/rating'
import { Tooltip } from '@mui/material'
import { primary500 } from 'styles/theme'

export const chipColors = {
    white: {
        bg: "white",
        color: primary500
    },
    dark: {
        bg: dark,
        color: "#FFFFFF"
    },
    thirdly: {
        bg: "#CBE1FF",
        color: "#1577FF"
    },
    gray: {
        bg: "#E0E0E0",
        color: "#757575"
    },
    gray2: {
        bg: "#CFCFCF",
        color: "#564F4F"
    },
    gray3: {
        bg: "#A5A5A5",
        color: "#EEEEEE"
    },
    gray4: {
        bg: "#f4f4f4",
        color: "#A5A5A5"
    },
    purple: {
        color: "#6E5DC6",
        bg: "#E9DBFC"
    },
    purple2: {
        color: "#E6D9FB",
        bg: "#5D49C2"
    },
    purple3: {
        color: "#EDE9FE",
        bg: "#6D28D9"
    },
    purple4: {
        color: "#F7DBFF",
        bg: "#86198F"
    },
    secondary: {
        bg: lightSecondary,
        color: secondary
    },
    pink: {
        bg: "#FFEBF5",
        color: "#EC54A3"
    },
    pink2: {
        bg: "#FFDBED",
        color: "#DA3D8E"
    },
    pink3: {
        bg: "#FFD9DC",
        color: "#F33B63"
    },
    pink4: {
        bg: "#FFC5D1",
        color: "#9D174D",
    },
    pink5: {
        bg: "#FFD0EA",
        color: "#9D174D",
    },
    error: {
        bg: "#FBD9DA",
        color: "#FF2618"
    },
    yellow: {
        bg: "#F4F1BD",
        color: "#AB9603"
    },
    green: {
        bg: "#E4F2D1",
        color: "#85A81F"
    },
    green2: {
        bg: "#D7F0E2",
        color: "#0D7B5F"
    },
    green3: {
        bg: "#D8FDEB",
        color: "#047857"
    },
    green4: {
        bg: "#C8F1F3",
        color: "#155E75"
    },
    green5: {
        bg: "#C7FADC",
        color: "#15803D"
    },
    green6: {
        bg: "#CCEBFF",
        color: "גבוהה"
    },
    green7: {
        bg: "#F0F9FF",
        color: "#0369A1"
    },
    green8: {
        bg: "#E3F6EC",
        color: "#3A8659"
    },
    orange: {
        bg: "#FEE4D8",
        color: "#FB7401"
    },
    orange2: {
        bg: "#FEF3C7",
        color: "#B45309"
    },
    orange3: {
        bg: "#FFE4C1",
        color: "#C2410C"
    },
    orange4: {
        bg: "#FFECA0",
        color: "#92400E"
    },
    lightBlue: {
        bg: "#CBE1FF",
        color: "#1577FF"
    },
    lightBlue2: {
        bg: "#C4EEF1",
        color: "#0E7490"
    },
    lightBlue3: {
        bg: "#C3E7FF",
        color: "#0284C7"
    },
    lightBlue4: {
        bg: "#E0E7FF",
        color: "#4338CA"
    },
    lightBlue5: {
        bg: "#C6D4FF",
        color: "#4F46E5"
    },
    lightBlue6: {
        bg: "#EAF3FF",
        color: dark
    },
    rating1: {
        color: ratingTexts2[0],
        bg: ratingColors7[0]
    },
    rating2: {
        color: ratingTexts2[1],
        bg: ratingColors7[1]
    },
    rating3: {
        color: ratingTexts2[2],
        bg: ratingColors7[2]
    },
    rating4: {
        color: ratingTexts2[3],
        bg: ratingColors7[3]
    },
    rating5: {
        color: ratingTexts2[4],
        bg: ratingColors7[4]
    }

}

/**
 * Chip (styled label/tag)
 * Small colored label for statuses, ratings, metadata, and inline tags across the app.
 *
 * **How it works**
 * - Pass a preset via `color` (key from `chipColors`, e.g. `thirdly`, `rating3`, `error`).
 * - Or pass a raw CSS color string — used directly when the key is not in `chipColors`.
 * - Background comes from the preset; override with `bg` when using a custom color string.
 * - Wraps content in an optional MUI `Tooltip` when `tooltip` is set.
 *
 * Exported as `Chip` from `components/Form`. Internal name: `CustomChip`.
 * `chipColors` is also exported — used for status/rating color maps elsewhere.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Chip content (text, icon, etc.).
 * @param {string} [props.color] - Preset key from `chipColors` or a raw text color value.
 * @param {string} [props.bg] - Custom background when `color` is not a preset key.
 * @param {string} [props.tooltip] - Tooltip text on hover.
 * @param {string} [props.size] - Chip size forwarded to styled component.
 * @param {string} [props.type] - Chip type variant (e.g. `icon`).
 * @param {boolean} [props.transparent] - Transparent background style.
 * @param {boolean} [props.round] - Rounded pill style.
 * @param {string} [props.borderColor] - Border color (passed as `border` to styled Chip).
 * @example
 * <Chip color="thirdly">Active</Chip>
 * @example
 * <Chip color="rating2">Expected range</Chip>
 * @example
 * <Chip color="#FF0000" bg="#FFE0E0" tooltip="Custom status">Draft</Chip>
 */
const CustomChip = (props) => {
    const { tooltip, type, size, transparent, bg, children, borderColor: border, round, color } = props

    const currentColor = chipColors[color]?.color || color
    const bgColor = chipColors[color]?.bg || bg

    return (
        <Tooltip placement='top' title={tooltip}>
            <Chip size={size} type={type} round={round} transparent={transparent} className={"chip"} color={currentColor} bg={bgColor}>
                {children}
            </Chip>
        </Tooltip>
    )
}

export default CustomChip